// The replacement step's delivery date (2026-11-02, Jenn; @cxhub/shared's
// replacement-edd.js, server/src/ai/replacement-edd.js). Pure helpers for
// SuggestionPanel.jsx, tested in ui/test/replacementEdd.test.js.
//
// The step is estimated when it is staged (step.deliveryEstimate). When the
// person changes its items, quantities, ship-to ZIP or shipping method, the
// panel asks the storefront again (debounced, one call per change), shows
// the new date on the step, and updates the reply's date value in place
// (2026-11-03). A step with no staged estimate (made before this existed,
// or with the flag off at generation) is looked up once when it is opened
// (2026-11-03); the server answers { estimate: null } with the flag off, and
// the step is then left alone (state.disabled).

import {
  replacementEddValues, replacementEddKey, isStagedEddRecord, stagedQuote, stagedEddLabel, stagedEddWarning, stagedEddSentenceLine, swapReplyDeliveryDates, replyDateUpdatedNote,
} from '@cxhub/shared'

export const REPLACEMENT_EDD_DEBOUNCE_MS = 700

// The step's starting record per index: the latest re-estimate in the
// Activity Log (stagedReplacementEstimate with the step's index), else the
// one made at generation. Only process_replacement steps that were staged
// (the flag was on): { [index]: { estimate } }.
export function initialEddStates(actions = [], apiCalls = []) {
  const out = {}
  ;(actions ?? []).forEach((a, i) => {
    if (a?.type !== 'process_replacement' || !a.deliveryEstimate) return
    let rec = a.deliveryEstimate
    for (const c of apiCalls ?? []) {
      if (c?.stepIndex === i && isStagedEddRecord(c?.stagedReplacementEstimate)) rec = c.stagedReplacementEstimate
    }
    out[i] = { estimate: rec }
  })
  return out
}

// The request for the step's current inputs, or null when there is nothing
// to estimate (not staged, no item selected). { key, selection }: selection
// carries the ZIP only, never the rest of the address.
export function eddRequestFor(step, input) {
  if (step?.type !== 'process_replacement') return null
  const values = replacementEddValues(step, input && typeof input === 'object' ? input : null)
  if (!values.items.length) return null
  return {
    key: replacementEddKey(values),
    selection: { items: values.items, shippingMethod: values.shippingMethod ?? '', zipCode: values.zipCode ?? '' },
  }
}

// Whether the step needs a new estimate for these inputs: its record was
// made for other values (or none yet), and no call for them is running.
export function needsReestimate(step, input, state = null) {
  const req = eddRequestFor(step, input)
  if (!req) return false
  // The flag was off when asked (the server's { estimate: null }).
  if (state?.disabled) return false
  if (state?.pendingKey === req.key) return false
  const rec = state?.estimate ?? step.deliveryEstimate
  if (isStagedEddRecord(rec) && replacementEddKey(rec.input ?? {}) === req.key) return false
  // A failed re-estimate for the same values is not repeated on its own.
  if (state?.failedKey === req.key) return false
  return true
}

// What the step shows: { label, sentence, notes, warning, note, loading }.
//   label     the date only ("Estimated delivery 11/06/2026");
//   sentence  no date, the reply quotes the storefront's own message;
//   notes     the record's step notes ([{ tone, text }]: out of stock and
//             the options that have a date, ships from the vendor, the
//             product page's text outside the 48);
//   warning   nothing to quote: the reply has INSERTEDD;
//   note      what the last re-estimate did to the reply (state.replyNote).
export function eddStepView(step, state = null) {
  if (step?.type !== 'process_replacement') return null
  const rec = state?.estimate ?? step.deliveryEstimate ?? null
  const loading = Boolean(state?.pendingKey)
  if (!rec && !loading) return null
  const note = state?.replyNote ?? null
  if (!isStagedEddRecord(rec)) return { label: null, sentence: null, notes: [], warning: null, note, loading }
  return { label: stagedEddLabel(rec), sentence: stagedEddSentenceLine(rec), notes: Array.isArray(rec.notes) ? rec.notes : [], warning: stagedEddWarning(rec), note, loading }
}

// The out-of-stock warning with the estimate's no-date text in it (one
// warning, not two). Returns { lines, merged }.
// The text joined to it: the no-date warning, else the out-of-stock notes.
export function stockLinesWithEdd(stockLines = [], view = null) {
  const lines = [...(stockLines ?? [])]
  const extra = view?.warning ? [view.warning] : (view?.notes ?? []).filter((n) => n.tone === 'warning').map((n) => n.text)
  if (!lines.length || !extra.length) return { lines, merged: false }
  for (const t of extra) if (!lines.includes(t)) lines.push(t)
  return { lines, merged: true }
}

// The reply after a re-estimate (Jenn, 2026-11-03), whether or not the
// person edited it: only the date values change, in place. A new date
// replaces the earlier one (and a placeholder on the Estimated Delivery
// line); no date makes it INSERTEDD, so Send refuses the reply until the
// person fills it. A reply with no date line is left as it is.
// { text, changed, note }.
export function replyAfterReestimate(text, previous, next) {
  const r = swapReplyDeliveryDates(text, previous, next)
  return { ...r, note: r.changed ? replyDateUpdatedNote(next) : null }
}

// The step whose date the reply box carries: the replacement on the
// resolution path in effect (the person's choice, else the preselection),
// else the first replacement step with no path. -1 when none.
export function mainReplacementIndex(actions = [], currentPath = null) {
  const list = actions ?? []
  const choice = list.find((a) => a?.type === 'task_path_choice' && a.params?.kind === 'resolution')
  if (choice) {
    const path = currentPath ?? choice.params?.preselected?.value ?? null
    return list.findIndex((a) => a?.type === 'process_replacement' && a.taskPath === path)
  }
  const own = list.findIndex((a) => a?.type === 'process_replacement' && !a.taskPath)
  return own >= 0 ? own : list.findIndex((a) => a?.type === 'process_replacement')
}

// The step's colours (Jenn, 2026-11-03): bold green when the reply has a
// date; bold red when it does not (a storefront message, a missing option,
// an error). 'success' | 'error' | null.
export function eddTone(view) {
  if (!view || view.loading) return null
  if (view.label) return 'success'
  if (view.sentence || view.warning || (view.notes ?? []).some((n) => n.tone === 'warning')) return 'error'
  return null
}

// The theme colour for a tone, readable in light and dark mode.
export function eddToneColor(theme, tone) {
  const p = theme?.palette?.[tone]
  if (!p) return undefined
  return theme.palette.mode === 'dark' ? p.light : p.dark
}

// The note box's line (2026-11-03): whenever the step's estimate is set or
// changes (staging, the lookup on open, a re-estimate, a pick from the
// stored answer), the line is added when the note has none, so it is
// always visible before Process. A key per record: the line is added once
// for that estimate; a line the person deletes comes back with the next
// estimate, and at Process.
export function eddNoteKey(record) {
  if (!isStagedEddRecord(record)) return null
  return [record.estimatedAt, record.input?.method ?? record.method ?? '', stagedQuote(record) ?? '', record.noDate?.kind ?? ''].join('|')
}
